<script setup>
import { ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TemplatesAPI from 'dashboard/api/whatsapp/templates';
import WhatsAppTabsHeader from './components/WhatsAppTabsHeader.vue';
import TemplateStatusBadge from './components/TemplateStatusBadge.vue';
import TemplateForm from './components/TemplateForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useWhatsappInbox } from './composables/useWhatsappInbox';

const { t } = useI18n();
const { whatsappInboxes, selectedInboxId, hasWhatsappInbox } =
  useWhatsappInbox();

const templates = ref([]);
const isLoading = ref(false);
const isSyncing = ref(false);
const templateFormRef = ref(null);
const deleteDialogRef = ref(null);
const templateToDelete = ref(null);

const loadTemplates = async () => {
  if (!selectedInboxId.value) return;
  isLoading.value = true;
  try {
    const { data } = await TemplatesAPI.getTemplates(selectedInboxId.value);
    templates.value = data.templates || [];
  } catch (error) {
    useAlert(t('WHATSAPP_SETTINGS.TEMPLATES.LIST.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const syncTemplates = async () => {
  if (!selectedInboxId.value) return;
  isSyncing.value = true;
  try {
    const { data } = await TemplatesAPI.syncTemplates(selectedInboxId.value);
    templates.value = data.templates || [];
    useAlert(t('WHATSAPP_SETTINGS.TEMPLATES.LIST.SYNC_SUCCESS'));
  } catch (error) {
    useAlert(t('WHATSAPP_SETTINGS.TEMPLATES.LIST.SYNC_ERROR'));
  } finally {
    isSyncing.value = false;
  }
};

const onCreateTemplate = async payload => {
  try {
    await TemplatesAPI.createTemplate({
      inboxId: selectedInboxId.value,
      template: payload,
    });
    templateFormRef.value?.close();
    useAlert(t('WHATSAPP_SETTINGS.TEMPLATES.FORM.CREATE_SUCCESS'));
    await loadTemplates();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('WHATSAPP_SETTINGS.TEMPLATES.FORM.CREATE_ERROR')
    );
  }
};

const confirmDelete = template => {
  templateToDelete.value = template;
  deleteDialogRef.value?.open();
};

const onDeleteTemplate = async () => {
  try {
    await TemplatesAPI.deleteTemplate({
      inboxId: selectedInboxId.value,
      name: templateToDelete.value.name,
    });
    useAlert(t('WHATSAPP_SETTINGS.TEMPLATES.LIST.DELETE_SUCCESS'));
    await loadTemplates();
  } catch (error) {
    useAlert(t('WHATSAPP_SETTINGS.TEMPLATES.LIST.DELETE_ERROR'));
  } finally {
    deleteDialogRef.value?.close();
    templateToDelete.value = null;
  }
};

watch(selectedInboxId, loadTemplates);
onMounted(loadTemplates);
</script>

<template>
  <div class="flex flex-col w-full gap-6">
    <WhatsAppTabsHeader
      v-model:inbox-id="selectedInboxId"
      active-tab="templates"
      :inboxes="whatsappInboxes"
    />

    <div
      v-if="!hasWhatsappInbox"
      class="flex flex-col items-center justify-center gap-2 py-16 text-center"
    >
      <p class="text-sm text-n-slate-11">
        {{ t('WHATSAPP_SETTINGS.NO_INBOX') }}
      </p>
    </div>

    <template v-else>
      <div class="flex items-center justify-end gap-2">
        <Button
          variant="ghost"
          icon="i-lucide-refresh-cw"
          :is-loading="isSyncing"
          :label="t('WHATSAPP_SETTINGS.TEMPLATES.LIST.SYNC')"
          @click="syncTemplates"
        />
        <Button
          icon="i-lucide-plus"
          :label="t('WHATSAPP_SETTINGS.TEMPLATES.LIST.CREATE')"
          @click="templateFormRef?.open()"
        />
      </div>

      <div v-if="isLoading" class="py-12 text-sm text-center text-n-slate-11">
        {{ t('WHATSAPP_SETTINGS.TEMPLATES.LIST.LOADING') }}
      </div>

      <div
        v-else-if="!templates.length"
        class="py-12 text-sm text-center text-n-slate-11"
      >
        {{ t('WHATSAPP_SETTINGS.TEMPLATES.LIST.EMPTY') }}
      </div>

      <table v-else class="w-full text-sm">
        <thead>
          <tr class="text-left text-n-slate-11 border-b border-n-weak">
            <th class="py-2 font-medium">
              {{ t('WHATSAPP_SETTINGS.TEMPLATES.LIST.NAME') }}
            </th>
            <th class="py-2 font-medium">
              {{ t('WHATSAPP_SETTINGS.TEMPLATES.LIST.CATEGORY') }}
            </th>
            <th class="py-2 font-medium">
              {{ t('WHATSAPP_SETTINGS.TEMPLATES.LIST.LANGUAGE') }}
            </th>
            <th class="py-2 font-medium">
              {{ t('WHATSAPP_SETTINGS.TEMPLATES.LIST.STATUS') }}
            </th>
            <th class="py-2" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="template in templates"
            :key="template.id || template.name"
            class="border-b border-n-weak"
          >
            <td class="py-3 font-medium text-n-slate-12">
              {{ template.name }}
            </td>
            <td class="py-3 text-n-slate-11">{{ template.category }}</td>
            <td class="py-3 text-n-slate-11">{{ template.language }}</td>
            <td class="py-3">
              <TemplateStatusBadge :status="template.status" />
            </td>
            <td class="py-3 text-right">
              <Button
                variant="ghost"
                color="ruby"
                size="sm"
                icon="i-lucide-trash-2"
                @click="confirmDelete(template)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <TemplateForm ref="templateFormRef" @submit="onCreateTemplate" />

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('WHATSAPP_SETTINGS.TEMPLATES.LIST.DELETE_TITLE')"
      :description="t('WHATSAPP_SETTINGS.TEMPLATES.LIST.DELETE_CONFIRM')"
      :confirm-button-label="t('WHATSAPP_SETTINGS.TEMPLATES.LIST.DELETE')"
      @confirm="onDeleteTemplate"
    />
  </div>
</template>
